import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import deviceBreakpointsType from '@johnlewispartnership/wtr-content-component-library/dist/component-library/constants/data-shapes/device-breakpoints';
import urls from 'constants/urls';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMarketingBadge } from 'hooks/use-marketing-badge';
import { SHOW_PRODUCT_TAGS } from 'constants/monetateExperiments';

import servingInstructions from 'constants/servingInstructions';
import { builderGrid } from 'components/Builder/helpers';

import { conflictType } from 'components/Conflict/propTypes';
import { ENTERTAINING } from 'constants/productTypes';
import { clearTimeout } from 'utils/settimeout-wrapper';
import { getPDPUrl, isPersonalisableProduct } from 'utils/product';

import ProductPodCounter from 'components/ProductPod/ProductPodCounter/ProductPodCounter';
import DeleteFromList from 'components/ProductPod/DeleteFromList';
import Favourite from 'components/Product/Favourite/Favourite';
import ProductBadge from 'components/Product/Badge';

// TODO: [SSR][WARN] Does this impact SSR?
// eslint-disable-next-line no-restricted-imports
import { mediaBreakpointCurrent } from 'utils/mediaQueries';

import { useExperiments } from 'components/Experiment/useExperiments';
import { ONE_POD_CQRESPONSIVE } from 'constants/podSizing';
import { dataLayer } from 'analytics/data-layer';
import { usePodImpression } from 'hooks/use-pod-impression';
import { useSetScrollState } from 'hooks/use-set-scroll-state';
import { usePodClickTracking } from 'hooks/use-pod-click-tracking';
import {
  makePodDataset,
  POD_TYPE_CLICK_TO_BUY,
  POD_TYPE_REGULAR,
  POD_TYPE_SPONSORED,
} from 'components/ProductPod2/utils';
import { BREAKPOINTS } from 'constants/grid';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import ProductTags from 'components/ProductTags';
import { useWtrSelector } from 'redux/hooks';
import { getProductPromotions } from 'redux/modules/product-details/selectors/product-pricing';
import { getPromotionsByIds } from 'redux/modules/entities/selectors/promotions';
import isEqual from 'lodash/isEqual';

import amendingOrder from 'redux/modules/trolley/selectors/amending-order';
import { getShowSlidedownAdvert } from './getShowSlidedownAdvert';
import ProductPodAttributes from './Attributes';
import ProductPodHeader from './Header';
import ProductPodImage from './Image';
import TrolleyControls from './TrolleyControls';
import Slidedown from './Slidedown';
import SlidedownContent from './SlidedownContent';

import styles from './ProductPod.scss';

const productPodDefaultPropAddRemoveFavourite = () => {};
const productPodDefaultPropAttributes = {};
const productPodDefaultPropLocation = {};
const productPodDefaultPropMetadata = {};
const productPodDefaultPropPositions = {};
const productPodDefaultPropToggleListMembership = () => {};
const productPodDefaultPropMarketingBadges = [];

const ProductPod = ({
  lineNumber: lineNumberUnMemoed,
  addRemoveFavourite = productPodDefaultPropAddRemoveFavourite,
  attributes = productPodDefaultPropAttributes,
  autoFocus = false,
  conflict = null,
  cookingStatus,
  className = '',
  cqResponsive = ONE_POD_CQRESPONSIVE,
  fullSize = false,
  hideUneditable = false,
  isEditable = true,
  isFavourite = false,
  isBuilderPage = false,
  listId,
  location = productPodDefaultPropLocation,
  isInMealDeal = false,
  orderId,
  positions = productPodDefaultPropPositions,
  productTags,
  searchType = '',
  sponsored = false,
  toggleListMembership = productPodDefaultPropToggleListMembership,
  optimisticTrolleyQuantityAmount,
  optimisticTrolleyQuantityUom,
  isDisabled,
  wrapperId = '',
  onSlidedownToggle,
  slidedownAdvertDetails,
  multibuyPromotionPath,
  depositCharge = null,
  displayPriceEstimated,
  displayPriceQualifier,
  productId,
  productName,
  productType,
  searchCategory,
  searchTags,
  thumbnail,
  currentSaleUnitPrice,
  marketingBadges = productPodDefaultPropMarketingBadges,
  maxPersonalisedMessageLength = 0,
  sponsorshipId,
  tracking,
  metadata = productPodDefaultPropMetadata,
  hasPromotions = false,
  listDefinitions,
}) => {
  const isPersonalisable = isPersonalisableProduct({ maxPersonalisedMessageLength });
  const productPosition = positions[mediaBreakpointCurrent()] ?? undefined;

  const observeRef = usePodImpression(
    {
      metadata,
      position: productPosition,
      productId,
      searchType,
      wrapperId,
      sponsorshipId,
      trackingUrls: tracking?.impressionUrls,
      vendor: tracking?.vendor,
      listDefinitions,
    },
    sponsored,
  );

  const podCallbackRef = useCallback(
    node => {
      observeRef.current = node;

      if (autoFocus) {
        node?.focus();
      }
    },
    [autoFocus, observeRef],
  );

  const { deviceBreakpoints = [] } = cqResponsive || ONE_POD_CQRESPONSIVE;

  const { getDecisionById } = useExperiments();

  const [isOpen, setIsOpen] = useState(false);
  const [added, setIsAdded] = useState(false);

  const lineNumber = useMemo(() => lineNumberUnMemoed, [lineNumberUnMemoed]);

  const fullCurrentPathname = location.pathname.includes(urls.search)
    ? location.pathname + location.search
    : location.pathname;
  const active = optimisticTrolleyQuantityAmount > 0;
  const isEntertaining = productType === ENTERTAINING;

  const { showAdvert, trackExperimentView: trackAdvertExperimentView } = getShowSlidedownAdvert({
    currentPathname: fullCurrentPathname,
    getDecisionById,
    slidedownAdvertDetails,
  });

  const displaySlidedown = showAdvert;

  const wrappedSetIsOpen = useCallback(
    value => {
      setIsOpen(value);
      onSlidedownToggle?.(value);
    },
    [onSlidedownToggle],
  );

  useEffect(() => {
    if (fullSize) {
      const closeSlidedown = () => wrappedSetIsOpen(false);

      document.addEventListener('slickSlideChanged', closeSlidedown);

      return () => {
        document.removeEventListener('slickSlideChanged', closeSlidedown);
      };
    }

    return undefined;
  }, [fullSize, wrappedSetIsOpen]);

  useEffect(() => {
    if (optimisticTrolleyQuantityAmount === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      wrappedSetIsOpen(false);
      setIsAdded(false);
    }
  }, [optimisticTrolleyQuantityAmount, wrappedSetIsOpen]);

  useEffect(() => {
    if (!added || showAdvert) {
      return () => {};
    }

    return () => {};
  }, [added, wrappedSetIsOpen, showAdvert]);

  const { showMarketingBadges, marketingBadgesFiltered } = useMarketingBadge(marketingBadges);
  const promotionIds = useWtrSelector(state => getProductPromotions(state, productId), isEqual);
  const promotions = useWtrSelector(
    state => getPromotionsByIds(state, promotionIds || []),
    isEqual,
  );
  const sponsoredOrRegular = sponsored ? POD_TYPE_SPONSORED : POD_TYPE_REGULAR;
  const podType = fullSize ? POD_TYPE_CLICK_TO_BUY : sponsoredOrRegular;
  const isAmending = useWtrSelector(amendingOrder);

  const { reportClickToAdTech, reportAnalyticsClick } = usePodClickTracking({
    conflictMessage: conflict?.messages?.shortItem?.toLowerCase(),
    isFavourite,
    position: productPosition,
    productId,
    searchType,
    wrapperId,
    boosted: false,
    sponsored,
    sponsorshipId,
    listDefinitions,
    orderId,
    promotions,
    podType,
    purchaseOrAmend: isAmending ? 'amend' : 'purchase',
    tracking,
  });

  const { setScrollState } = useSetScrollState(lineNumber);

  const displayClasses = useMemo(() => {
    const cqSizing = deviceBreakpoints
      .map(({ name, visible } = {}) =>
        visible ? `visible-${name === 'default' ? 'xl' : name}-flex` : null,
      )
      .filter(breakpoint => breakpoint)
      .join(' ');
    const onePodSizing = [styles.productPod]
      .concat(
        deviceBreakpoints
          .map(
            ({ name, width } = {}) =>
              `col-${name === 'default' ? 'xl' : name}-${
                isBuilderPage ? builderGrid[name] : width
              }of12`,
          )
          .filter(breakpoint => breakpoint),
      )
      .join(' ');

    return classNames(
      {
        [cqSizing]: deviceBreakpoints.length > 0,
        [onePodSizing]: !fullSize,
        [styles.fullSize]: fullSize,
        [styles.noDeviceBreakpoints]: deviceBreakpoints.length < 1,
      },
      className,
    );
  }, [className, isBuilderPage, deviceBreakpoints, fullSize]);
  const toggleOptions = () => wrappedSetIsOpen(!isOpen);

  const toggleFavourite = useCallback(() => {
    if (!isFavourite) {
      reportClickToAdTech();
    }
    addRemoveFavourite(isFavourite, lineNumber);
  }, [addRemoveFavourite, isFavourite, lineNumber, reportClickToAdTech]);

  const removeFromList = () => {
    dataLayer.push({
      event: 'list-delete-button-click',
      lineNumber,
      listId,
    });
    toggleListMembership({
      lineNumber,
      shoppingListId: listId,
    });
  };

  const setAdded = useCallback(
    isAdded => {
      setIsAdded(isAdded);
      reportClickToAdTech();

      if (!isAdded) {
        return;
      }

      if (showAdvert) {
        wrappedSetIsOpen(true);
        trackAdvertExperimentView(true);
      }
    },
    [reportClickToAdTech, showAdvert, trackAdvertExperimentView, wrappedSetIsOpen],
  );

  const onProductClick = useCallback(() => {
    setScrollState();
    reportAnalyticsClick();
  }, [setScrollState, reportAnalyticsClick]);

  const productPodAttributesProps = useMemo(
    () => ({
      attributes,
      cookingStatus,
      entertaining: isEntertaining,
      fullSize,
      productName,
      productId,
      productType,
      searchType,
      reportAnalyticsClick,
      currentSaleUnitPrice,
    }),
    [
      attributes,
      cookingStatus,
      fullSize,
      isEntertaining,
      productId,
      productName,
      productType,
      reportAnalyticsClick,
      searchType,
      currentSaleUnitPrice,
    ],
  );

  const hideTags =
    getDecisionById(SHOW_PRODUCT_TAGS.experiment).variant === SHOW_PRODUCT_TAGS.variant.hideTags;

  if (hideUneditable && !isEditable) return null;

  const productUrl = productId ? getPDPUrl(productId, productName) : '';

  return (
    <article
      data-testid="product-pod"
      {...makePodDataset({
        hasPromotions,
        lineNumber,
        name: productName,
        productType,
        productPosition,
        conflictMessage: conflict?.messages?.shortItem?.toLowerCase(),
        podType,
        sponsored,
        searchType,
        searchTags,
        searchCategory,
        vendor: tracking?.vendor,
      })}
      className={displayClasses}
      id={`tPp${productId}`}
      ref={podCallbackRef}
      tabIndex={-1}
    >
      <div
        className={classNames(styles.icons, {
          [styles.activeIcons]: active,
          [styles.hasBadge]: sponsored,
        })}
      >
        <div className={classNames(styles.badgeContainer)}>
          {(sponsored || showMarketingBadges) && (
            <ProductBadge text={sponsored ? 'Sponsored' : marketingBadgesFiltered[0].name} />
          )}
          {listId && !showMarketingBadges && <DeleteFromList removeFromList={removeFromList} />}
        </div>
        {listId && showMarketingBadges ? (
          <DeleteFromList removeFromList={removeFromList} />
        ) : (
          <Favourite
            toggleFavourite={toggleFavourite}
            isFavourite={isFavourite}
            productName={productName}
          />
        )}
      </div>
      <div
        className={classNames(styles.content, {
          [styles.isEntertaining]: isEntertaining,
          [styles.inTrolley]: active,
        })}
      >
        <section className={styles.details}>
          <SSRMediaQuery minWidth={BREAKPOINTS.sm}>
            <div
              className={classNames(styles.desktopCounter, {
                [styles.desktopCounterActive]: active,
                [styles.optionsOpen]: isOpen,
                [styles.inTrolley]: active,
              })}
            >
              <ProductPodCounter
                {...{
                  added,
                  trolleyQuantityAmount: optimisticTrolleyQuantityAmount,
                  trolleyQuantityUom: optimisticTrolleyQuantityUom,
                  productName,
                  isInMealDeal,
                }}
              />
              {displaySlidedown && (
                <Slidedown
                  {...{
                    active,
                    displayToggle: true,
                    isOpen,
                    toggleOptions,
                    wrappedSetIsOpen,
                  }}
                >
                  <SlidedownContent
                    {...{
                      isOpen,
                      multibuyPromotionPath,
                      showAdvert,
                      slidedownAdvertDetails,
                    }}
                  />
                </Slidedown>
              )}
            </div>
          </SSRMediaQuery>
          <ProductPodImage
            position={productPosition}
            onClick={onProductClick}
            fullSize={fullSize}
            name={productName}
            productId={productId}
            thumbnail={thumbnail}
            disabled={isDisabled}
            isEntertaining={isEntertaining}
          />
          <div className={styles.headerStarWrapper}>
            <ProductPodHeader
              onClick={onProductClick}
              fullSize={fullSize}
              name={productName}
              productId={productId}
              withoutSpacing={hideTags}
            >
              <ProductTags productTags={productTags} />
            </ProductPodHeader>
            <div
              className={classNames(
                styles.podSectionAttributes,
                'visible-xs-block',
                'visible-sm-block',
                'visible-md-block',
                'visible-lg-block',
                'visible-xl-block',
                { [styles.hideTags]: hideTags },
              )}
              data-test="product-pod-content"
            >
              <ProductPodAttributes {...productPodAttributesProps} />
            </div>
          </div>
        </section>
        <section
          data-testid="product-pod-trolley-choices"
          className={classNames({
            [styles.fullSizeTrolleyControls]: fullSize,
            [styles.trolleyChoices]: !fullSize,
          })}
        >
          <TrolleyControls
            {...{
              productName,
              conflict,
              isPersonalisableProduct: isPersonalisable,
              productUrl,
              productId,
              depositCharge,
              displayPriceEstimated,
              displayPriceQualifier,
              isDisabled,
              orderId,
              setAdded,
              lineNumber,
              productPosition,
              searchType,
              listDefinitions,
            }}
          />
        </section>
        <SSRMediaQuery maxWidth={BREAKPOINTS.sm}>
          <section
            className={classNames(styles.mobileCounter, styles.optionsOpen, {
              [styles.mobileCounterActive]: active,
              [styles.noSlidedown]: !displaySlidedown,
            })}
          >
            <ProductPodCounter
              {...{
                added,
                trolleyQuantityAmount: optimisticTrolleyQuantityAmount,
                trolleyQuantityUom: optimisticTrolleyQuantityUom,
                productName,
                isInMealDeal,
              }}
            />
            {displaySlidedown && (
              <Slidedown>
                <SlidedownContent
                  {...{
                    isOpen,
                    multibuyPromotionPath,
                    showAdvert,
                    slidedownAdvertDetails,
                  }}
                />
              </Slidedown>
            )}
          </section>
        </SSRMediaQuery>
      </div>
    </article>
  );
};

ProductPod.displayName = 'ProductPod';

ProductPod.propTypes = {
  addRemoveFavourite: PropTypes.func,
  attributes: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  autoFocus: PropTypes.bool,
  lineNumber: PropTypes.string.isRequired,
  className: PropTypes.string,
  conflict: conflictType,
  cookingStatus: PropTypes.oneOf(Object.keys(servingInstructions)),
  cqResponsive: {
    deviceBreakpoints: deviceBreakpointsType(),
  },
  fullSize: PropTypes.bool,
  hideUneditable: PropTypes.bool,
  isBuilderPage: PropTypes.bool,
  isEditable: PropTypes.bool,
  isFavourite: PropTypes.bool,
  listId: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  isInMealDeal: PropTypes.bool,
  orderId: PropTypes.string,
  positions: PropTypes.shape({
    lg: PropTypes.number,
    md: PropTypes.number,
    sm: PropTypes.number,
    xl: PropTypes.number,
    xs: PropTypes.number,
  }),

  metadata: PropTypes.shape({
    recToken: PropTypes.string,
    monetateId: PropTypes.string,
  }),
  maxPersonalisedMessageLength: PropTypes.number,
  sponsorshipId: PropTypes.string,
  hasPromotions: PropTypes.bool,
  searchType: PropTypes.string,
  sponsored: PropTypes.bool,
  toggleListMembership: PropTypes.func,
  optimisticTrolleyQuantityAmount: PropTypes.number.isRequired,
  optimisticTrolleyQuantityUom: PropTypes.string.isRequired,
  wrapperId: PropTypes.string,
  isDisabled: PropTypes.bool.isRequired,
  onSlidedownToggle: PropTypes.func,
  slidedownAdvertDetails: PropTypes.shape({
    experimentKey: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    logoUrl: PropTypes.string,
    iconName: PropTypes.string,
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  multibuyPromotionPath: PropTypes.string,
  depositCharge: PropTypes.shape({
    amount: PropTypes.number.isRequired,
  }),
  displayPriceEstimated: PropTypes.bool.isRequired,
  displayPriceQualifier: PropTypes.string,
  productId: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  productType: PropTypes.string.isRequired,
  searchCategory: PropTypes.string.isRequired,
  searchTags: PropTypes.string.isRequired,
  thumbnail: PropTypes.string.isRequired,
  marketingBadges: PropTypes.arrayOf(PropTypes.shape({})),
  tracking: PropTypes.shape({
    vendor: PropTypes.string,
    impressionUrls: PropTypes.arrayOf(PropTypes.string),
    clickUrls: PropTypes.arrayOf(PropTypes.string),
  }),
  listDefinitions: PropTypes.shape({
    curation: PropTypes.string,
    format: PropTypes.string,
    page: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default memo(ProductPod);
